import React, { useState, useEffect } from 'react';
import ListingCard from './ListingCard';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ResultsGrid = ({ listings, loading, hasMore, onLoadMore }) => {
  const [displayedListings, setDisplayedListings] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setDisplayedListings(listings);
  }, [listings]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    await onLoadMore();
    setIsLoadingMore(false);
  };

  if (loading && displayedListings?.length === 0) {
    return (
      <div className="space-y-6">
        {/* Loading Skeleton */}
        {[...Array(6)]?.map((_, index) => (
          <div key={index} className="bg-card rounded-lg shadow-organic overflow-hidden animate-pulse">
            <div className="h-48 bg-muted"></div>
            <div className="p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
              <div className="h-3 bg-muted rounded w-full"></div>
              <div className="h-3 bg-muted rounded w-2/3"></div>
              <div className="flex space-x-2">
                <div className="h-6 bg-muted rounded-full w-16"></div>
                <div className="h-6 bg-muted rounded-full w-12"></div>
              </div>
              <div className="flex justify-between items-center">
                <div className="h-4 bg-muted rounded w-16"></div>
                <div className="h-4 bg-muted rounded w-12"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!loading && displayedListings?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
          <Icon name="Search" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
          No results found
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          We couldn't find any listings matching your search criteria. Try adjusting your filters or search terms.
        </p>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground font-medium">
            Suggestions:
          </p>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Try searching with broader terms</li>
            <li>• Check your spelling</li>
            <li>• Remove some filters</li>
            <li>• Expand your search area</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {displayedListings?.map((listing) => (
          <ListingCard key={listing?.id} listing={listing} />
        ))}
      </div>
      {/* Load More */}
      {hasMore && (
        <div className="text-center pt-8">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            loading={isLoadingMore}
            iconName="ChevronDown"
            iconPosition="right"
            size="lg"
          >
            {isLoadingMore ? 'Loading more...' : 'Load More Results'}
          </Button>
        </div>
      )}
      {/* End of Results */}
      {!hasMore && displayedListings?.length > 0 && (
        <div className="text-center pt-8 pb-4">
          <div className="inline-flex items-center space-x-2 text-muted-foreground">
            <div className="h-px bg-border flex-1 w-12"></div>
            <span className="text-sm">You've seen all results</span>
            <div className="h-px bg-border flex-1 w-12"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsGrid;